import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";

import getChartColorsArray from "../../../Components/Common/ChartsDynamicColor";
import moment from "moment";

function escapeTooltipText(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const YAxis = ({ dataColors, weeklyOrders, weeklyVisits }) => {
  const chartDatalabelsBarColors = getChartColorsArray(dataColors);

  // Get today's date for comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Merge and align data from both sources
  const mergedData = useMemo(() => {
    const ordersArray = weeklyOrders || [];
    const visitsArray = weeklyVisits?.daily_breakdown || [];

    // Create a map of all unique dates using normalized date strings
    const dateMap = new Map();

    // Normalize date to YYYY-MM-DD format
    const normalizeDate = (dateStr) => {
      if (!dateStr) return null;

      // Replace forward slashes with dashes to standardize format
      const standardizedDateStr = dateStr.replace(/\//g, '-');

      // Parse the date string directly without timezone conversion
      const parts = standardizedDateStr.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1].padStart(2, '0');
        const day = parts[2].padStart(2, '0');
        return `${year}-${month}-${day}`;
      }

      return null;
    };

    // Add orders data
    ordersArray.forEach(order => {
      const dateKey = normalizeDate(order.date || order.day);
      if (dateKey) {
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, {
            date: dateKey,
            ordered: 0,
            delivered: 0,
            visit_count: 0
          });
        }
        dateMap.get(dateKey).ordered = parseInt(order.ordered) || 0;
        dateMap.get(dateKey).delivered = parseInt(order.delivered) || 0;
      }
    });

    // Add visits data
    visitsArray.forEach(visit => {
      const dateKey = normalizeDate(visit.date);
      if (dateKey) {
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, {
            date: dateKey,
            ordered: 0,
            delivered: 0,
            visit_count: 0
          });
        }
        dateMap.get(dateKey).visit_count = parseInt(visit.visit_count) || 0;
      }
    });

    // Convert map to sorted array
    const result = Array.from(dateMap.values()).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    return result;
  }, [weeklyOrders, weeklyVisits]);

  const series = [
    {
      name: "Visits",
      type: "bar",
      data: mergedData.map((item) => item.visit_count),
    },
    {
      name: "Ordered",
      type: "bar",
      data: mergedData.map((item) => item.ordered),
    },
    {
      name: "Delivered",
      type: "bar",
      data: mergedData.map((item) => item.delivered),
    },

  ];

  const options = {
    chart: {
      height: 350,
      type: "line",
    },

    colors: chartDatalabelsBarColors,
    xaxis: {
      categories: mergedData.map((item) => {
        const itemDate = new Date(item.date);
        itemDate.setHours(0, 0, 0, 0);

        // Check if this date is today
        if (itemDate.getTime() === today.getTime()) {
          return "Today";
        }
        // Format as DD/MM for other dates
        return moment(item.date).format("DD/MM");
      }),
    },
    yaxis: [
      {
        title: {
          text: "Orders",
        },
        seriesName: "Ordered",
        labels: {
          formatter: (value) => value.toFixed(0),
        },
      },
      {
        seriesName: "Delivered",
        show: false,
        labels: {
          formatter: (value) => value.toFixed(0),
        },
      },
      {
        title: {
          text: "Visits",
        },
        seriesName: "Visits",
        opposite: true,
        labels: {
          formatter: (value) => value.toFixed(0),
        },
      },
    ],
    stroke: {
      width: [0, 0, 3],
    },
  };

  return (
    <ReactApexChart
      dir="ltr"
      options={options}
      series={series}
      type="line"
      height={350}
    />
  );
};

const CustomDataLabel = ({ dataColors, weeklyServices }) => {
  // Create a fallback series if data is loading or empty
  const seriesData = weeklyServices?.map((service) => service?.order_count);

  const categories = weeklyServices?.map((service) => service?.item);

  const chartDatalabelsBarColors = getChartColorsArray(dataColors);

  const series = [
    {
      data: seriesData || [],
    },
  ];

  const options = {
    chart: {
      type: "bar",
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        barHeight: "100%",
        distributed: true,
        horizontal: true,
        dataLabels: {
          position: "bottom",
        },
      },
    },
    colors: chartDatalabelsBarColors,
    dataLabels: {
      enabled: true,
      textAnchor: "start",
      style: {
        colors: ["#fff"],
      },
      formatter: function (val, opt) {
        return val + ":  " + opt.w.globals.labels[opt.dataPointIndex];
      },
      offsetX: 0,
      dropShadow: {
        enabled: false,
      },
    },
    stroke: {
      width: 1,
      colors: ["#fff"],
    },
    xaxis: {
      categories: categories || [],
    },
    yaxis: {
      labels: {
        show: false,
      },
    },

    // subtitle: {
    //   text: "Category Names as DataLabels inside bars",
    //   align: "center",
    // },
    tooltip: {
      custom: ({ dataPointIndex, w }) => {
        const service = weeklyServices?.[dataPointIndex];
        if (!service) return "";

        const color =
          w?.globals?.colors?.[dataPointIndex] ||
          chartDatalabelsBarColors?.[dataPointIndex] ||
          "#878a99";
        const item = escapeTooltipText(service.item);
        const total = service.order_count ?? 0;
        const optionLines = (service.options ?? [])
          .map((opt) => {
            const optionName = escapeTooltipText(opt.option_name);
            const choiceName = escapeTooltipText(opt.choice_name);
            return `<div style="margin-top:2px;padding-left:16px;">${optionName} - ${choiceName}: ${opt.order_count}</div>`;
          })
          .join("");

        return `
          <div style="padding:8px 10px;font-size:12px;line-height:1.4;">
            <div style="display:flex;align-items:center;gap:6px;font-weight:600;">
              <span style="width:10px;height:10px;border-radius:50%;background:${color};display:inline-block;flex-shrink:0;"></span>
              <span>${item}: ${total}</span>
            </div>
            ${optionLines}
          </div>
        `;
      },
    },
  };

  return (
    <React.Fragment>
      <ReactApexChart
        dir="ltr"
        className="apex-charts"
        options={options}
        series={series}
        type="bar"
        height={350}
      />
    </React.Fragment>
  );
};

const DeliveredOrdersChart = ({ dailyStats, dataColors }) => {
  var chartColumnDistributedColors = getChartColorsArray(dataColors);

  const series = [
    {
      name: "Visits",
      data: dailyStats.map((kitchen) => ({
        x: kitchen.kitchen_name,
        y: kitchen.visit_count,
      })),
    },
    {
      name: "Ordered Items",
      data: dailyStats.map((kitchen) => ({
        x: kitchen.kitchen_name,
        y: kitchen.ordered_orders === 0 ? null : kitchen.ordered_orders, // Use null instead of 0
      })),
    },
    {
      name: "Delivered Items",
      data: dailyStats.map((kitchen) => ({
        x: kitchen.kitchen_name,
        y: kitchen.delivered_orders === 0 ? null : kitchen.delivered_orders, // Use null instead of 0
      })),
    },
  ];

  var options = {
    chart: {
      height: 350,
      type: "bar",
      toolbar: {
        show: false,
      },
    },
    colors: chartColumnDistributedColors,
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "45%",
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        // Only show label if value is greater than 0 and not null to prevent distortion
        if (val === null || val === undefined || val === 0) {
          return '';
        }
        return Number(val).toFixed(0);
      },
      offsetY: 0,
      offsetX: 0,
      position: 'top',
      style: {
        fontSize: '12px',
        fontWeight: 600,
      },
      hideOverlappingLabels: true,
    },
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '14px',
      fontFamily: 'inherit',

    },
    yaxis: {
      title: {
        text: "Orders / Visits",
      },
      labels: {
        formatter: function (val) {
          return Number(val).toFixed(0);
        },
      },
    },
    xaxis: {
      type: "category",
      group: {
        style: {
          fontSize: "10px",
          fontWeight: 700,
        },
      },
    },
    title: {
      text: "",
      style: {
        fontWeight: 500,
      },
    },
    tooltip: {
      // x: {
      //     formatter: function (val) {
      //         return "Q" + dayjs(val).quarter() + " " + dayjs(val).format("YYYY")
      //     }
      // }
    },
  };

  return (
    <ReactApexChart
      dir="ltr"
      className="apex-charts"
      series={series}
      options={options}
      type="bar"
      height={350}
    />
  );
};
const DeliveryTimeChart = ({ dailyStats, dataColors }) => {
  const chartColors = getChartColorsArray(dataColors);

  const categories = dailyStats.map((k) => k.kitchen_name);

  const formatSecondsToMinSec = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}m ${s}s`;
  };

  const series = [
    {
      name: "Min Delivery Time",
      data: dailyStats.map((k) => k.min_delivery_time),
    },
    {
      name: "Avg Delivery Time",
      data: dailyStats.map((k) => k.avg_delivery_time),
    },
    {
      name: "Max Delivery Time",
      data: dailyStats.map((k) => k.max_delivery_time),
    },
  ];

  const options = {
    chart: {
      height: 350,
      type: "bar",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "45%",
        endingShape: "rounded",
      },
    },
    colors: chartColors,
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        // Only show label if value is greater than 0 and not null
        if (val === null || val === undefined || val === 0) {
          return null;
        }
        return Number(val / 60).toFixed(0);
      },
      offsetY: 0,
      offsetX: 0,
      style: {
        fontSize: '12px',
        fontWeight: 600,
      },
    },
    xaxis: {
      categories: categories,
      labels: {
        rotate: -45,
      },
    },
    yaxis: {
      title: {
        text: "Delivery Time",
      },
      labels: {
        formatter: function (val) {
          const m = Math.floor(val / 60);
          const s = Math.round(val % 60);
          return `${m}m ${s}s`;
        },
      },
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return formatSecondsToMinSec(val);
        },
      },
    },
    title: {
      text: "Delivery Time per Kitchen",
      style: {
        fontWeight: 500,
      },
    },
  };

  return (
    <ReactApexChart
      dir="ltr"
      className="apex-charts"
      series={series}
      options={options}
      type="bar"
      height={350}
    />
  );
};

const formatSecondsToMinSec = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
};

const ResponseTimeChart = ({ responseTimeStats, dataColors }) => {
  const chartColors = getChartColorsArray(dataColors);
  const stats = responseTimeStats || [];

  const series = [
    {
      name: "Min Response Time",
      data: stats.map((k) => k.min_response_time),
    },
    {
      name: "Avg Response Time",
      data: stats.map((k) => k.avg_response_time),
    },
    {
      name: "Max Response Time",
      data: stats.map((k) => k.max_response_time),
    },
  ];

  const options = {
    chart: {
      height: 350,
      type: "bar",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "45%",
        endingShape: "rounded",
      },
    },
    colors: chartColors,
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        if (val === null || val === undefined || val === 0) {
          return null;
        }
        return Number(val / 60).toFixed(0);
      },
      style: {
        fontSize: "12px",
        fontWeight: 600,
      },
    },
    xaxis: {
      categories: stats.map((k) => k.kitchen_name),
      labels: {
        rotate: -45,
      },
    },
    yaxis: {
      title: {
        text: "Response Time",
      },
      labels: {
        formatter: (val) => formatSecondsToMinSec(val),
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val) =>
          val === null || val === undefined ? "--" : formatSecondsToMinSec(val),
      },
      x: {
        formatter: (val, { dataPointIndex }) => {
          const row = stats[dataPointIndex];
          if (!row) return val;
          return `${val} (${row.actioned_items} handled, ${row.pending_items} waiting)`;
        },
      },
    },
    noData: {
      text: "No staff actions recorded yet",
    },
  };

  return (
    <ReactApexChart
      dir="ltr"
      className="apex-charts"
      series={series}
      options={options}
      type="bar"
      height={350}
    />
  );
};

const WeeklyResponseTimeChart = ({ weeklyResponseTime, dataColors }) => {
  const chartColors = getChartColorsArray(dataColors);
  const days = weeklyResponseTime || [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const series = [
    {
      name: "Avg Response Time",
      data: days.map((d) => d.avg_response_time),
    },
    {
      name: "Max Response Time",
      data: days.map((d) => d.max_response_time),
    },
  ];

  const options = {
    chart: {
      height: 350,
      type: "line",
      toolbar: { show: false },
    },
    colors: chartColors,
    stroke: {
      curve: "smooth",
      width: 3,
    },
    markers: {
      size: 4,
    },
    xaxis: {
      categories: days.map((d) => {
        const date = moment(d.day, "YYYY/MM/DD");
        if (date.isSame(today, "day")) {
          return "Today";
        }
        return date.format("DD/MM");
      }),
    },
    yaxis: {
      title: {
        text: "Response Time",
      },
      labels: {
        formatter: (val) =>
          val === null || val === undefined ? "" : formatSecondsToMinSec(val),
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val, { dataPointIndex }) => {
          if (val === null || val === undefined) return "--";
          const count = days[dataPointIndex]?.actioned_items ?? 0;
          return `${formatSecondsToMinSec(val)} (${count} items)`;
        },
      },
    },
  };

  return (
    <ReactApexChart
      dir="ltr"
      className="apex-charts"
      series={series}
      options={options}
      type="line"
      height={350}
    />
  );
};

const WeeklyVisitsChart = ({ dataColors, visitWeekly }) => {
  const chartColors = getChartColorsArray(dataColors);

  const dailyBreakdown = visitWeekly?.daily_breakdown || [];

  // Get today's date for comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const series = [
    {
      name: "QR Scans",
      type: "area",
      data: dailyBreakdown.map((day) => day.visit_count || 0),
    },
  ];

  const options = {
    chart: {
      height: 350,
      type: "area",
      toolbar: {
        show: false,
      },
    },
    colors: chartColors,
    dataLabels: {
      enabled: true,
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 90, 100],
      },
    },
    xaxis: {
      categories: dailyBreakdown.map((day) => {
        const dayDate = new Date(day.date);
        dayDate.setHours(0, 0, 0, 0);

        // Check if this date is today
        if (dayDate.getTime() === today.getTime()) {
          return "Today";
        }
        // Format as DD/MM for other dates
        return moment(day.date).format("DD/MM");
      }),
    },
    yaxis: {
      title: {
        text: "QR Scans",
      },
      labels: {
        formatter: (value) => value.toFixed(0),
      },
    },
    tooltip: {
      y: {
        formatter: (value) => value + " scans",
      },
    },
  };

  return (
    <ReactApexChart
      dir="ltr"
      options={options}
      series={series}
      type="area"
      height={350}
    />
  );
};

const TopSpacesChart = ({ dataColors, visitSpaces }) => {
  const chartColors = getChartColorsArray(dataColors);

  const spaces = visitSpaces?.spaces || [];
  const topSpaces = [...spaces]
    .sort((a, b) => b.visit_count - a.visit_count)
    .slice(0, 10);

  const series = [
    {
      data: topSpaces.map((space) => space.visit_count),
    },
  ];

  const options = {
    chart: {
      type: "bar",
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        barHeight: "100%",
        distributed: true,
        horizontal: true,
        dataLabels: {
          position: "bottom",
        },
      },
    },
    colors: chartColors,
    dataLabels: {
      enabled: true,
      textAnchor: "start",
      style: {
        colors: ["#fff"],
      },
      formatter: function (val, opt) {
        return val + ":  " + opt.w.globals.labels[opt.dataPointIndex];
      },
      offsetX: 0,
      dropShadow: {
        enabled: false,
      },
    },
    stroke: {
      width: 1,
      colors: ["#fff"],
    },
    xaxis: {
      categories: topSpaces.map((space) => space.space_name || "Unknown"),
    },
    yaxis: {
      labels: {
        show: false,
      },
    },
    tooltip: {
      x: { show: false },
      y: {
        formatter: function (val, { seriesIndex, dataPointIndex, w }) {
          const label = w.globals.labels[dataPointIndex];
          return `${label}: ${val} scans`;
        },
        title: {
          formatter: () => "",
        },
      },
    },
  };

  return (
    <ReactApexChart
      dir="ltr"
      className="apex-charts"
      options={options}
      series={series}
      type="bar"
      height={350}
    />
  );
};

export {
  YAxis,
  CustomDataLabel,
  DeliveryTimeChart,
  DeliveredOrdersChart,
  WeeklyVisitsChart,
  TopSpacesChart,
  ResponseTimeChart,
  WeeklyResponseTimeChart,
};
